import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Button from '@material-ui/core/Button';
import { useTranslation } from "react-i18next";
import { getDocsUrl } from '../../helpers/docsUrl';
import { set as setInStorage, STORAGE_DOMAINS } from '../../services/storage/GQStorage';

const useStyles = makeStyles(() => ({
    grow: {
        flexGrow: 1,
        width: '96%',
        margin: '0 auto 28px',
        minHeight: '260px',
        padding: '23px 16px 14px',
    },
    description: {
        marginBottom: '16px',
        fontSize: '14px',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
    },
    typeSelector: {
        textDecoration: 'none',
        marginTop: '15px',
    },
    textarea: {
        padding: '10px',
        fontSize: '14px',
        width: '100%',
        minHeight: '120px',
        boxSizing: 'border-box',
        resize: 'vertical',
        borderRadius: '0px 4px 4px 4px',
        border: '1px solid #dadadae6',
        marginTop: '-3px',
        marginLeft: '5px',
        fontFamily: 'Arial, Helvetica, sans-serif ,Helvetica Neue',
        color: '#777',
        fontWeight: '400',
        '&:hover': {
            border: '1px solid black',
        },
        '&:focus': {
            outline: 'none',
            border: '2px solid #337AB7',
        },
    },
    darkTextarea: {
        padding: '10px',
        fontSize: '14px',
        width: '100%',
        minHeight: '120px',
        boxSizing: 'border-box',
        resize: 'vertical',
        borderRadius: '0px 4px 4px 4px',
        border: '1px solid #dadadae6',
        marginTop: '-4px',
        backgroundColor: '#292929',
        marginLeft: '5px',
        color: 'white',
        '&:hover': {
            border: '1px solid black',
        },
        '&:focus': {
            outline: 'none',
            border: '2px solid #337AB7',
        },
    },
    dropDown: {
        margin: '3px 7px 0 7px',
        backgroundColor: '#f5f5f5',
        width: '60px',
        border: '1px solid grey',
        borderRadius: '4px',
    },
    errorBorder: {
        border: '1px solid red !important',
        '&:focus': {
            outline: 'none !important',
            border: '2px solid red !important',
        },
    },
    errorMessage: {
        fontStyle: 'italic',
        color: 'red !important',
        fontSize: '12px',
        marginTop: '4px',
        marginLeft: '4px',
    },
    warningMessage: {
        fontSize: '12px',
        marginTop: '4px',
        marginLeft: '4px',
        fontStyle: 'italic',
    },
    searchButton: {
        marginTop: '19px !important',
    },
    loginSubmitCancel: {
        marginTop: '19px !important',
        backgroundColor: '#0182C5',
        borderColor: '#1F4E79',
        border: '1px solid #1F4E79',
        color: 'white',
        margin: "4px",
        float: 'right',
        textTransform: 'capitalize',
        '&:hover': {
            backgroundColor: '#0182C5',
            boxShadow: 'none',
        },
    },
}));

const NUCLEOTIDE_IUPAC_REGEX = /^[ATGCUN0-9]*$/i;

const validateFastaNucleotide = (input) => {
    // Remove FASTA headers (lines starting with '>')
    const lines = input
        .split('\n')
        .filter(line => line && !line.startsWith('>'))
        .map(l => l.trim());

    // Join lines and remove ALL whitespace (spaces, tabs, newlines)
    const seq = lines.join('').replace(/\s+/g, '');

    if (!NUCLEOTIDE_IUPAC_REGEX.test(seq)) {
        // Find illegal characters (anything not in allowed set)
        // const badChars = seq
        //     .replace(/[ATGCUN0-9]/gi, '')
        //     .split('')
        //     .filter((v, i, a) => a.indexOf(v) === i)
        //     .join(', ');
        return `Wrong query sequence type. It does not look like nucleotide.`;
    }

    return '';
};


function AlignSequencesDirectly() {
    const classes = useStyles();
    const { t } = useTranslation('common');
    const [bestAlignment, setBestAlignment] = useState(100);
    const [querySeqType, setQuerySeqType] = useState('nucleotide');
    const [subjectSeqType, setSubjectSeqType] = useState('nucleotide');
    const [queryInputValue, setQueryInputValue] = useState('');
    const [subjectInputValue, setSubjectInputValue] = useState('');
    const [strategy, setStrategy] = useState("Genepast");
    const [wordSize, setWordSize] = useState();
    const [scoringMatrix, setScoringMatrix] = useState('');
    const [gapOpeningPenalty, setGapOpeningPenalty] = useState(10);
    const [gapExtensionPenalty, setGapExtensionPenalty] = useState(1);
    const toggle = useSelector(state => state.setToggle.toggle);
    const [manuallyChangedSubject, setManuallyChangedSubject] = useState(false);
    const history = useHistory();
    const [queryError, setQueryError] = useState('');
    const [subjectError, setSubjectError] = useState('');
    const [queryDuplicateWarning, setQueryDuplicateWarning] = useState('');
    const [subjectDuplicateWarning, setSubjectDuplicateWarning] = useState('');

    useEffect(() => {
        if (querySeqType === 'nucleotide' && subjectSeqType === 'nucleotide') {
            setScoringMatrix('NUC.3.1');
            setWordSize(11);
        } else if (querySeqType === 'protein' || subjectSeqType === 'protein') {
            setScoringMatrix('BLOSUM62');
            setWordSize(3);
        }
    }, [querySeqType, subjectSeqType]);

    const handleQuerySeqTypeChange = (type) => {
        setQuerySeqType(type);
        if (!manuallyChangedSubject) setSubjectSeqType(type);

        if (type === 'nucleotide') {
            const result = validateFastaInputFull(queryInputValue, 'nucleotide');
            setQueryError(result.error);
            setQueryDuplicateWarning(result.warning);
        } else if (type === 'protein') {
            setQueryError('');
            setQueryDuplicateWarning('');
        }
    };

    const handleSubjectSeqTypeChange = (type) => {
        setSubjectSeqType(type);
        setManuallyChangedSubject(false);

        if (type === 'nucleotide') {
            const result = validateFastaInputFull(subjectInputValue, 'nucleotide');
            setSubjectError(result.error);
            setSubjectDuplicateWarning(result.warning);
        } else if (type === 'protein') {
            setSubjectError('');
            setSubjectDuplicateWarning('');
        }
    };

    // FASTA formatting & duplicate checks 
    const validateFastaInput = (input) => {
        if (!input.trim()) return { error: 'Sequence cannot be empty.', warning: '' };
        const lines = input.trim().split('\n');
        const sequences = [];
        let currentSeq = { id: '', content: '' };
        for (let line of lines) {
            line = line.trim();
            if (line.startsWith('>')) {
                if (currentSeq.id) sequences.push(currentSeq);
                currentSeq = { id: line, content: '' };
            } else {
                currentSeq.content += line;
            }
        }
        if (currentSeq.id) sequences.push(currentSeq);
        const ids = sequences.map(seq =>
            seq.id.replace(/\s+/g, '').trim().toLowerCase()
        );
        // Normalize contents: remove all whitespace before lowercasing for duplicate detection
        const normalizedContents = sequences.map(seq => seq.content.replace(/\s+/g, '').toLowerCase());

        // Duplicate ID check
        const duplicateId = ids.find((id, idx) => ids.indexOf(id) !== idx);
        if (duplicateId) return { error: `Duplicate sequence ID found: ${duplicateId}`, warning: '' };

        // Duplicate content check after normalization - return as warning, not error
        const duplicateContent = normalizedContents.find((content, idx) => normalizedContents.indexOf(content) !== idx);
        if (duplicateContent) return { error: '', warning: `Duplicate sequence found: ${duplicateContent}` };
        return { error: '', warning: '' };
    };

    function validateFastaInputFull(input, seqType) {
        let result = validateFastaInput(input);
        if (!result.error && seqType === 'nucleotide') {
            const nucleotideError = validateFastaNucleotide(input);
            if (nucleotideError) {
                result.error = nucleotideError;
            }
        }
        return result;
    }

    function handleSubmit() {
        const qResult = validateFastaInputFull(queryInputValue, querySeqType);
        const sResult = validateFastaInputFull(subjectInputValue, subjectSeqType);

        setQueryError(qResult.error);
        setSubjectError(sResult.error);
        setQueryDuplicateWarning(qResult.warning);
        setSubjectDuplicateWarning(sResult.warning);
        if (qResult.error || sResult.error) return;

        const payload = {
            best: bestAlignment,
            qdb_seq_type: querySeqType,
            qdb: queryInputValue,
            sdb_seq_type: subjectSeqType,
            sdb: subjectInputValue,
            strat_name: strategy.toLowerCase(),
        };
        if (strategy === 'Blast') {
            payload.word_size = wordSize;
            payload.scoring_matrix = scoringMatrix;
        } else if (strategy === 'Smith-Waterman') {
            payload.gapo = gapOpeningPenalty;
            payload.gape = gapExtensionPenalty;
            payload.scoring_matrix = scoringMatrix;
            payload.strat_name = 'sw';
        }
        setInStorage('params', payload, STORAGE_DOMAINS.SESSION);

        history.push({
            pathname: "/alignSequencesDirectly/Alignments",
            state: payload
        });
    }

    function homePage() {
        history.push('/home');
    }
    return (
        <div className={classes.grow}>
            <div className={classes.description}>
                <a href={getDocsUrl('align_two_sequences.html')} target="_blank" rel="noopener noreferrer"><b>Align sequence(s)</b></a> in Query area with sequence(s) in Subject area. You can paste in either a single sequence or multiple sequences in FASTA format in both input areas, and alignments will be computed between the two sets, but not within the set.
            </div>
            <br />
            {/* Query Section */}
            <div className={classes.section}>
                <div>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Query</span>
                    <span style={{ fontSize: '14px', marginLeft: '7px' }}>
                        (Keep only
                        <select
                            className={classes.dropDown}
                            value={bestAlignment}
                            onChange={(e) => setBestAlignment(e.target.value)}
                        >
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                        best alignment(s) per query sequence)
                    </span>
                </div>
                <div className={classes.typeSelector}>
                    <span
                        className={querySeqType === "nucleotide" ? "inactiveTab" : 'accountTabs'}
                        onClick={() => handleQuerySeqTypeChange('nucleotide')}
                    >
                        Nucleotide Sequences
                    </span>
                    <span
                        className={querySeqType === "protein" ? "inactiveTab" : 'accountTabs'}
                        onClick={() => handleQuerySeqTypeChange('protein')}
                    >
                        Protein Sequences
                    </span>
                </div>
                <textarea
                    className={`${toggle ? classes.textarea : classes.darkTextarea} ${queryError ? classes.errorBorder : ''}`}
                    placeholder={`Enter ${querySeqType} sequence here`}
                    value={queryInputValue}
                    onChange={(e) => {
                        const val = e.target.value;
                        setQueryInputValue(val);
                        const result = validateFastaInputFull(val, querySeqType);
                        setQueryError(result.error);
                        setQueryDuplicateWarning(result.warning);
                    }}
                    onBlur={() => {
                        const result = validateFastaInputFull(queryInputValue, querySeqType);
                        setQueryError(result.error);
                        setQueryDuplicateWarning(result.warning);
                    }}
                />
                {queryError && <p className={classes.errorMessage}>{queryError}</p>}
                {queryDuplicateWarning && (
                    <p className={classes.warningMessage}>
                        <span style={{ color: '#f44336' }}>WARNING: </span>
                        {queryDuplicateWarning}
                    </p>
                )}
                <center><strong>VS</strong></center>
            </div>

            {/* Subject Section */}
            <div className={classes.section}>
                <div style={{ marginBottom: '13px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Subject</span>
                </div>
                <div>
                    <span
                        className={subjectSeqType === "nucleotide" ? "inactiveTab" : "accountTabs"}
                        onClick={() => handleSubjectSeqTypeChange('nucleotide')}
                    >
                        Nucleotide Sequences
                    </span>
                    <span
                        className={subjectSeqType === "protein" ? "inactiveTab" : 'accountTabs'}
                        onClick={() => handleSubjectSeqTypeChange('protein')}
                    >
                        Protein Sequences
                    </span>
                </div>
                <textarea
                    className={`${toggle ? classes.textarea : classes.darkTextarea} ${subjectError ? classes.errorBorder : ''}`}
                    placeholder={`Enter ${subjectSeqType} sequence here`}
                    value={subjectInputValue}
                    onChange={(e) => {
                        const val = e.target.value;
                        setSubjectInputValue(val);
                        const result = validateFastaInputFull(val, subjectSeqType);
                        setSubjectError(result.error);
                        setSubjectDuplicateWarning(result.warning);
                    }}
                    onBlur={() => {
                        if (!subjectInputValue.trim()) {
                            setSubjectError('Subject sequence cannot be empty.');
                        }
                    }}
                />
                {subjectError && <p className={classes.errorMessage}>{subjectError}</p>}
                {subjectDuplicateWarning && (
                    <p className={classes.warningMessage}>
                        <span style={{ color: '#f44336' }}>WARNING: </span>
                        {subjectDuplicateWarning}
                    </p>
                )}
            </div>
            <br />
            {/* Strategy Section (no changes needed) */}
            <div>
                <div>
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Strategy</span>
                    <div>
                        <button
                            className={strategy === "Genepast" ? "inactiveTab" : "accountTabs"}
                            onClick={() => setStrategy("Genepast")}
                            style={{ marginLeft: '0px !important' }}
                        >
                            Genepast
                        </button>
                        <button
                            className={strategy === "Blast" ? "inactiveTab" : "accountTabs"}
                            onClick={() => setStrategy("Blast")}
                        >
                            Blast
                        </button>
                        <button
                            className={strategy === "Smith-Waterman" ? "inactiveTab" : "accountTabs"}
                            onClick={() => setStrategy("Smith-Waterman")}
                        >
                            Smith-Waterman
                        </button>
                    </div>
                </div>
                <div style={{ border: '1px solid #dadadae6', borderRadius: '0px 4px 4px 4px', marginLeft: '5px', marginTop: '-2px' }}>
                    {strategy === "Genepast" && (
                        <div style={{ margin: '11px 0px 54px 9px', fontSize: '14px' }}>
                            Use <a href={getDocsUrl('sequence_comparison_algorithms.html', 'genepast')} target="_blank" rel="noopener noreferrer">Genepast strategy</a> with minimum percent identity over alignment 65%.
                        </div>
                    )}
                    {strategy === "Blast" && (
                        <div style={{ margin: '11px 0px 24px 9px', fontSize: '14px' }}>
                            <p>Use <a href={getDocsUrl('sequence_comparison_algorithms.html', 'blast')} target="_blank" rel="noopener noreferrer">Blast strategy</a>, a local alignment algorithm.</p>
                            <p>Word Size
                                {subjectSeqType === 'nucleotide' && querySeqType === 'nucleotide' ?
                                    (<select
                                        className={classes.dropDown}
                                        value={wordSize}
                                        onChange={(e) => setWordSize(e.target.value)}
                                    >
                                        <option value="11">11</option>
                                        <option value="7">7</option>
                                        <option value="15">15</option>
                                    </select>) :
                                    (<select
                                        className={classes.dropDown}
                                        value={wordSize}
                                        onChange={(e) => setWordSize(e.target.value)}
                                    >
                                        <option value="3">3</option>
                                        <option value="2">2</option>
                                    </select>)}
                            </p>
                            <p>Scoring matrix
                                {subjectSeqType === 'nucleotide' && querySeqType === 'nucleotide' ?
                                    (<select
                                        className={classes.dropDown}
                                        style={{ width: '80px' }}
                                        value={scoringMatrix}
                                        onChange={(e) => setScoringMatrix(e.target.value)}>
                                        <option value="NUC.3.1">NUC.3.1</option>
                                        <option value="NUC.2.1">NUC.2.1</option>
                                        <option value="NUC.4.4">NUC.4.4</option>
                                    </select>) :
                                    (<select
                                        className={classes.dropDown}
                                        style={{ width: '100px' }}
                                        value={scoringMatrix}
                                        onChange={(e) => setScoringMatrix(e.target.value)}>
                                        <option value='BLOSUM62'>BLOSUM62</option>
                                        <option value='BLOSUM80'>BLOSUM80</option>
                                        <option value='BLOSUM45'>BLOSUM45</option>
                                        <option value='PAM30'>PAM30</option>
                                        <option value='PAM70'>PAM70</option>
                                    </select>)}
                            </p>
                        </div>
                    )}
                    {strategy === "Smith-Waterman" &&
                        <div style={{ margin: '11px 0px 25px 9px' }}>
                            <p>Use <a href="https://en.wikipedia.org/wiki/Smith%E2%80%93Waterman_algorithm" target="_blank" rel="noopener noreferrer">Smith-Waterman strategy</a>, a local alignment algorithm.</p>
                            <p>Gap Openning penalty
                                <select
                                    className={classes.dropDown}
                                    value={gapOpeningPenalty}
                                    onChange={(e) => setGapOpeningPenalty(e.target.value)}>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="30">30</option>
                                    <option value="40">40</option>
                                    <option value="50">50</option>
                                </select>
                            </p>
                            <p>Gap Extension penalty
                                <select
                                    className={classes.dropDown}
                                    value={gapExtensionPenalty}
                                    onChange={(e) => setGapExtensionPenalty(e.target.value)}>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                </select>
                            </p>
                            <p>Scoring matrix
                                {subjectSeqType === 'nucleotide' && querySeqType === 'nucleotide' ?
                                    (<select
                                        className={classes.dropDown}
                                        style={{ width: '80px' }}
                                        value={scoringMatrix}
                                        onChange={(e) => setScoringMatrix(e.target.value)}>
                                        <option value="NUC.3.1">NUC.3.1</option>
                                        <option value="NUC.2.1">NUC.2.1</option>
                                        <option value="NUC.4.4">NUC.4.4</option>
                                    </select>) :
                                    (<select
                                        className={classes.dropDown}
                                        style={{ width: '100px' }}
                                        value={scoringMatrix}
                                        onChange={(e) => setScoringMatrix(e.target.value)}>
                                        <option value='BLOSUM62'>BLOSUM62</option>
                                        <option value='BLOSUM80'>BLOSUM80</option>
                                        <option value='BLOSUM45'>BLOSUM45</option>
                                        <option value='PAM30'>PAM30</option>
                                        <option value='PAM70'>PAM70</option>
                                    </select>)}
                            </p>
                        </div>}
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row-reverse' }} className="float-right mb-3">
                <Button
                    variant="contained"
                    className={`accountInfo ${classes.searchButton}`}
                    onClick={handleSubmit}
                >{t('Search')}</Button>
                <Button variant="contained" color={'default'} className={classes.loginSubmitCancel} onClick={homePage} type="submit">{t('cancel')}</Button>
            </div>
        </div>
    );
}

export default AlignSequencesDirectly;
